import { getAdmissionOptions, type FullOrderViewModel } from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { GoalsAndInterventionOrders } from '~/features/careplanning/goals-interventions/orders/GoalsAndInterventionOrders';
import { FrequencyOrders } from '~/features/frequencies/FrequencyOrders';
import { LabOrders } from '~/features/frequencies/labs/LabOrders';
import { NewSigningPhysicians } from './physicians/NewSigningPhysicians';
import { CopiedPhysicians } from './physicians/CopiedPhysicians';
import { Section } from '~/shared/section/Section';
import { SectionTitle } from '~/shared/section/SectionTitle';
import { FileText } from 'lucide-react';
import { OrderStatusPill } from './OrderStatusPill';
import { formatAddress } from '~/util/functions';
import { OrderPatientDetails } from './OrderPatientDetails';
import { FullOrderContextProvider } from './orders/FullOrderContext';
import { WoundOrders } from '../wounds/orders/WoundOrders';

export function Order({ fullOrder, showPatientDetails = false }: { fullOrder: FullOrderViewModel; showPatientDetails?: boolean }) {
	const { data: admission } = useSuspenseQuery(getAdmissionOptions(fullOrder.stayId));
	return (
		<FullOrderContextProvider fullOrder={fullOrder}>
			<div className="flex flex-col space-y-4">
				<div className={showPatientDetails ? 'grid grid-cols-3 gap-6' : 'grid grid-cols-1'}>
					<Section
						header={<SectionTitle title="Order Details" TitleIcon={FileText} status={<OrderStatusPill order={fullOrder} />} />}
						className="col-span-2"
					>
						<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-500">Medical Record No.</label>
									<p className="text-gray-900">{fullOrder.patient.patientIdentifier}</p>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-500">Provider No.</label>
									<p className="text-gray-900">{admission.agency.npiNumber}</p>
								</div>
							</div>

							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-500">Start of Care Date</label>
									<p className="text-gray-900">{admission.startOfCareDate.toLocaleDateString()}</p>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-500">Provider Name</label>
									<p className="text-gray-900">Gallagher Home Health Services</p>
								</div>
							</div>

							<div className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-500">Certification Period</label>
									<p className="text-gray-900">
										{fullOrder.certificationPeriod.startDate.toLocaleDateString()} -{' '}
										{fullOrder.certificationPeriod.endDate.toLocaleDateString()}
									</p>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-500">Provider Address</label>
									<p className="text-gray-900">{formatAddress(admission.branch.address)}</p>
								</div>
							</div>
						</div>
					</Section>
					{showPatientDetails && <OrderPatientDetails order={fullOrder} />}
				</div>
				<div className="grid grid-cols-2 gap-6">
					<NewSigningPhysicians />
					<CopiedPhysicians />
				</div>
				<div className="col-span-2 grid grid-cols-1 gap-6">
					<FrequencyOrders />
					<LabOrders />
					<GoalsAndInterventionOrders />
					<WoundOrders />
				</div>
			</div>
		</FullOrderContextProvider>
	);
}
